import { QRCodeSVG } from "qrcode.react";
import { Smartphone, LogOut, ArrowRight, CalendarDays, Plus, X, Clock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Schedule as ScheduleAPI } from "../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmProvider";

const APP_QR_URL =
  "https://app.emergent.sh/share-preview?app=exp%3A%2F%2Fcourier-nav-4.preview.emergentagent.com";

/**
 * Kierowca's portal home — they have zero portal perms, so we don't show them
 * Dashboard / Fleet / Finance. Just the QR code to install the mobile app where
 * they actually do their work.
 */
export default function KierowcaHome() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-8" data-testid="kierowca-home">
      <div className="max-w-2xl w-full">
        <div className="bg-white border-2 border-zinc-900 p-8 md:p-12 shadow-[12px_12px_0_0_#18181b]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-label mb-1">// GOPOSSIBLE · Kurier</div>
              <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">
                Witaj, {user?.name || user?.username}
              </h1>
            </div>
            <button
              onClick={logout}
              data-testid="kierowca-logout"
              className="inline-flex items-center gap-2 border-2 border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-brand"
            >
              <LogOut className="w-4 h-4" /> Wyloguj
            </button>
          </div>

          <p className="text-zinc-700 mb-8 leading-relaxed">
            Twoja praca odbywa się w <strong>aplikacji mobilnej GoPossible Courier</strong>.
            Portal webowy nie jest przeznaczony dla kurierów — pobierz appkę i zaloguj się
            tymi samymi danymi co tutaj.
          </p>

          <div className="grid md:grid-cols-[auto,1fr] gap-8 items-center">
            <div className="border-2 border-zinc-900 p-4 bg-white" data-testid="kierowca-qr">
              <QRCodeSVG
                value={APP_QR_URL}
                size={220}
                level="M"
                bgColor="#ffffff"
                fgColor="#18181B"
              />
            </div>
            <div className="space-y-4">
              <div className="font-label">// Pobierz aplikację</div>
              <h2 className="font-heading font-black text-xl uppercase tracking-tight leading-tight">
                Zeskanuj telefonem
              </h2>
              <ol className="space-y-2 text-sm text-zinc-700">
                <li><span className="text-brand font-bold">1.</span> Otwórz aparat lub czytnik QR w telefonie</li>
                <li><span className="text-brand font-bold">2.</span> Nakieruj na kod obok</li>
                <li><span className="text-brand font-bold">3.</span> Zainstaluj appkę GoPossible Courier</li>
                <li><span className="text-brand font-bold">4.</span> Zaloguj się tym samym loginem co tutaj</li>
              </ol>
              <a
                href={APP_QR_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="kierowca-app-link"
                className="inline-flex items-center gap-2 mt-4 bg-zinc-900 hover:bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider transition-colors"
              >
                <Smartphone className="w-4 h-4" /> Otwórz link
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-[11px] font-mono uppercase tracking-wider text-zinc-500">
          Powered by Ambitions
        </div>

        <KierowcaSchedule />
      </div>
    </div>
  );
}

const STATUS_LABEL = {
  pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-800 border-amber-400" },
  approved: { label: "Zatwierdzony", cls: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  rejected: { label: "Odrzucony", cls: "bg-red-100 text-red-800 border-red-400" },
  modified: { label: "Zmieniony przez koordynatora", cls: "bg-blue-100 text-blue-800 border-blue-400" },
};

function KierowcaSchedule() {
  const { confirm } = useConfirm();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    all_day: false,
    start_time: "08:00",
    end_time: "16:00",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ScheduleAPI.list();
      setSlots(res.slots || []);
    } catch (err) {
      toast.error("Nie udało się załadować grafiku");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await ScheduleAPI.create({
        date: form.date,
        all_day: form.all_day,
        start_time: form.all_day ? "" : form.start_time,
        end_time: form.all_day ? "" : form.end_time,
        notes: form.notes,
      });
      toast.success("Zgłoszenie wysłane — czeka na akceptację");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirm("Usunąć zgłoszenie?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await ScheduleAPI.remove(id);
      toast.success("Usunięto");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="mt-8 bg-white border-2 border-zinc-900 p-6 md:p-8 shadow-[8px_8px_0_0_#18181b]" data-testid="kierowca-schedule">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-label mb-1">// Grafik pracy</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight flex items-center gap-2">
            <CalendarDays className="w-6 h-6" /> Mój grafik
          </h2>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="kierowca-add-slot"
          className="inline-flex items-center gap-2 bg-brand text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-brand-hover"
        >
          <Plus className="w-4 h-4" /> Zgłoś dostępność
        </button>
      </div>

      {loading && <div className="py-6 text-center text-sm text-zinc-500">Ładowanie...</div>}
      {!loading && slots.length === 0 && (
        <div className="py-6 text-center text-sm text-zinc-500 border-2 border-dashed border-zinc-300">
          Brak zgłoszeń. Kliknij &bdquo;Zgłoś dostępność&rdquo;, aby dodać slot.
        </div>
      )}
      <div className="space-y-2">
        {slots.map((s) => {
          const meta = STATUS_LABEL[s.status] || STATUS_LABEL.pending;
          const timeLabel = s.all_day
            ? "Cały dzień"
            : s.start_time && s.end_time
            ? `${s.start_time}–${s.end_time}`
            : s.start_time || "—";
          return (
            <div
              key={s.id}
              className={`border ${meta.cls} px-3 py-2 flex items-center justify-between`}
              data-testid={`kierowca-slot-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-bold">{new Date(s.date).toLocaleDateString("pl-PL", { weekday: "long", day: "numeric", month: "long" })}</span>
                  <span className="text-xs font-mono flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {timeLabel}
                  </span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider mt-0.5">{meta.label}</div>
                {s.notes && <div className="text-xs italic text-zinc-600 mt-1">{s.notes}</div>}
                {s.review_notes && (
                  <div className="text-xs mt-1">
                    <span className="font-bold">Uwagi koordynatora:</span> {s.review_notes}
                  </div>
                )}
              </div>
              {s.status === "pending" && (
                <button
                  onClick={() => remove(s.id)}
                  data-testid={`kierowca-slot-remove-${s.id}`}
                  className="p-1.5 hover:bg-red-600 hover:text-white border border-red-400 text-red-600"
                  title="Usuń zgłoszenie"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
          data-testid="kierowca-form-overlay"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md border-2 border-zinc-900 shadow-[12px_12px_0_0_#18181b]"
            data-testid="kierowca-form"
          >
            <div className="flex items-start justify-between px-6 py-4 border-b-2 border-zinc-900">
              <h2 className="font-heading font-black text-xl uppercase tracking-tight">Zgłoś dostępność</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-zinc-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-label text-[10px] block mb-1">Data</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  data-testid="kierowca-form-date"
                  className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.all_day}
                  onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                  data-testid="kierowca-form-all-day"
                />
                <span>Cały dzień (bez konkretnych godzin)</span>
              </label>
              {!form.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-label text-[10px] block mb-1">Od</label>
                    <input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                      data-testid="kierowca-form-start"
                      className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="font-label text-[10px] block mb-1">Do</label>
                    <input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                      data-testid="kierowca-form-end"
                      className="w-full border-2 border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="font-label text-[10px] block mb-1">Notatka (opcjonalnie)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="kierowca-form-notes"
                  className="w-full border-2 border-zinc-900 px-3 py-2 text-sm focus:outline-none focus:border-brand"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t-2 border-zinc-900 flex gap-3">
              <button
                type="submit"
                disabled={busy}
                data-testid="kierowca-form-submit"
                className="flex-1 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover disabled:opacity-50"
              >
                {busy ? "Wysyłam..." : "Wyślij zgłoszenie"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
